/**
 * ListenView — the full-screen "Listen" mode, for a car mount and for the last half hour before sleep.
 *
 * Everything the reader page puts between a thumb and the narration is gone: one huge play/pause,
 * ⟲30 / 30⟳ (the skip that crosses pages and chapters, `useNarration.skipBy`), the chapter and the
 * time left in it, the sleep timer, and the two settings that only matter with the screen out of
 * reach — keep the screen on, and what the car's ⏮/⏭ buttons do.
 *
 * Always the `night` reader theme, whatever the book is set to: it is used in a dark car and a dark
 * bedroom, and a white sheet at full brightness is the wrong answer in both. Tokens only, through
 * `data-read-theme`, so nothing here hardcodes a colour (AGENTS.md *Conventions*).
 *
 * Rendered into `document.body` through a portal. The reader's stage is transformed for page turns,
 * and a `position: fixed` element inside a transformed ancestor is positioned against that ancestor —
 * the same trap `Reader.tsx` documents for its menus.
 *
 * It owns no clock. It lives inside `NarrationChrome`, which already re-renders twice a second while
 * playing, so the time left and the sleep countdown move with that tick and stop when it does.
 */

import { useEffect, useRef, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";

import type { SleepChoice, SleepState } from "../audio/sleepTimer.ts";
import { formatRemaining, SLEEP_CHOICES } from "../audio/sleepTimer.ts";
import type { TrackButtons } from "../lib/listenPrefs.ts";
import { formatRate, nextRate } from "./PlaybackBar.tsx";

/** The extra controls `useNarration` hands the reader. `null` in a room, which routes every control. */
export interface ListenControls {
  /** ±seconds on the chapter axis — crosses pages, and chapters at the edges. */
  skip(sec: number): void;
  sleep(): SleepState & { stopping: boolean };
  setSleep(choice: SleepChoice | null): void;
  keepAwakeSupported: boolean;
  keepAwake(): boolean;
  setKeepAwake(on: boolean): void;
  trackButtons(): TrackButtons;
  setTrackButtons(mode: TrackButtons): void;
}

export interface ListenViewProps {
  controls: ListenControls;
  playing: boolean;
  /** The voice has run out of page and the next is being made. The button says so instead of lying. */
  waiting: boolean;
  chapterTitle: string;
  msLeftInChapter: number | null;
  rate: number;
  onPlayPause(): void;
  onRate(next: number): void;
  onClose(): void;
}

export function ListenView(props: ListenViewProps): ReactElement | null {
  const { controls, playing, waiting, chapterTitle, msLeftInChapter, rate, onPlayPause, onRate, onClose } = props;
  // The controls read live values from the engine; this only forces a render after a change here.
  const [, bump] = useState(0);
  const refresh = (): void => bump((n) => n + 1);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [onClose]);

  // The wake lock is this view's: leaving it gives the screen back its sleep.
  useEffect(() => () => controls.setKeepAwake(false), [controls]);

  if (typeof document === "undefined") return null;

  const sleep = controls.sleep();
  const sleepNow =
    sleep.kind === "off"
      ? null
      : sleep.kind === "chapter"
        ? "Stops at the end of this chapter"
        : sleep.stopping
          ? "Stopping after this sentence"
          : `Stops in ${formatRemaining(sleep.remainingMs)}`;
  const armed = (c: SleepChoice): boolean =>
    c === "chapter" ? sleep.kind === "chapter" : sleep.kind === "minutes" && Math.round(sleep.totalMs / 60_000) === c;
  const track = controls.trackButtons();
  const awake = controls.keepAwake();

  return createPortal(
    <div className="ab-listen" data-read-theme="night" role="dialog" aria-modal="true" aria-label="Listening">
      <div className="ab-listen-top">
        <button ref={closeRef} type="button" className="ab-btn" onClick={onClose} aria-label="Back to the page">
          ← Page
        </button>
        <button
          type="button"
          className="ab-btn"
          onClick={() => onRate(nextRate(rate))}
          aria-label={`Speed: ${formatRate(rate)}. Tap for the next speed.`}
        >
          {formatRate(rate)}
        </button>
      </div>

      <div className="ab-listen-now" aria-live="polite">
        <div className="ab-listen-chapter">{chapterTitle}</div>
        <div className="ab-listen-left">
          {waiting ? "Making the next page — it starts on its own." : leftLabel(msLeftInChapter)}
        </div>
      </div>

      <div className="ab-listen-transport">
        <button type="button" className="ab-listen-skip" onClick={() => controls.skip(-30)} aria-label="Back 30 seconds">
          ⟲30
        </button>
        <button
          type="button"
          className="ab-listen-play"
          onClick={onPlayPause}
          aria-label={playing ? "Pause" : "Play"}
          aria-pressed={playing}
        >
          {playing ? "❚❚" : "▶"}
        </button>
        <button type="button" className="ab-listen-skip" onClick={() => controls.skip(30)} aria-label="Forward 30 seconds">
          30⟳
        </button>
      </div>

      <div className="ab-listen-settings">
        <div className="ab-field">
          <span className="ab-field-label">Sleep timer{sleepNow ? ` · ${sleepNow}` : ""}</span>
          <div className="ab-choices">
            {SLEEP_CHOICES.map((c) => (
              <button
                key={String(c)}
                type="button"
                className="ab-choice"
                aria-pressed={armed(c)}
                onClick={() => {
                  controls.setSleep(armed(c) ? null : c);
                  refresh();
                }}
              >
                {c === "chapter" ? "End of chapter" : `${c} min`}
              </button>
            ))}
            {sleep.kind !== "off" ? (
              <button
                type="button"
                className="ab-choice"
                onClick={() => {
                  controls.setSleep(null);
                  refresh();
                }}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </div>

        <div className="ab-field">
          <span className="ab-field-label">Car / headphone ⏮ ⏭ buttons</span>
          <div className="ab-choices">
            {(["skip", "chapter"] as const).map((m) => (
              <button
                key={m}
                type="button"
                className="ab-choice"
                aria-pressed={track === m}
                onClick={() => {
                  controls.setTrackButtons(m);
                  refresh();
                }}
              >
                {m === "skip" ? "Skip 30 s" : "Chapter"}
              </button>
            ))}
          </div>
        </div>

        {controls.keepAwakeSupported ? (
          <div className="ab-field">
            <span className="ab-field-label">Screen</span>
            <div className="ab-choices">
              <button
                type="button"
                className="ab-choice"
                aria-pressed={awake}
                onClick={() => {
                  controls.setKeepAwake(!awake);
                  refresh();
                }}
              >
                {awake ? "Keeping the screen on" : "Keep the screen on"}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

function leftLabel(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms <= 0) return "";
  const mins = Math.round(ms / 60_000);
  return mins < 1 ? "Under a minute left in this chapter" : `${mins} min left in this chapter`;
}

export default ListenView;
